import { useQuery, UseQueryOptions } from "@tanstack/react-query";

import { ChangelogComment } from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { authHeader } from "@/utils/headers";

export interface ChangelogCommentsResponse {
  success: boolean;
  items: ChangelogComment[];
  page: number;
  pages: number;
  total: number;
  per_page: number;
  has_next: boolean;
  has_prev: boolean;
}

interface UseGetTopLevelCommentsParams {
  workspaceId: number;
  changelogId: number;
  page?: number;
  perPage?: number;
}

export const useGetTopLevelComments = (
  { workspaceId, changelogId, page = 1, perPage = 50 }: UseGetTopLevelCommentsParams,
  options?: Omit<UseQueryOptions<ChangelogCommentsResponse, Error>, "queryKey" | "queryFn">,
) => {
  const userToken = useAuth()?.userToken ?? "";

  return useQuery<ChangelogCommentsResponse, Error>({
    queryKey: ["changelogComments", workspaceId, changelogId, page, perPage],
    queryFn: async () => {
      const params = new URLSearchParams({
        changelog_id: changelogId.toString(),
        page: page.toString(),
        per_page: perPage.toString(),
      });

      const response = await fetch(
        `${ENDPOINTS.changelog_comments(workspaceId)}?${params.toString()}`,
        {
          method: "GET",
          headers: authHeader(userToken),
        },
      );

      return handleResponse(response);
    },
    enabled: !!workspaceId && !!changelogId && !!userToken,
    ...options,
  });
};
